/**
 * Auth bridge — reads tokens out of the in-process store
 * (`authTokenStore.js`) and orchestrates refresh on 401. Used by both
 * `restClient.authedFetch` and `analyticsClient`.
 *
 * Tokens are no longer stored in `localStorage`. The fetch shim
 * (`authFetchShim.js`) captures tokens out of `/v1/auth/*` response
 * bodies into the store; this module reads from it. Bearer header
 * attachment is also handled by the shim, so callers do not need to
 * `Authorization`-decorate requests themselves.
 *
 * Refresh handling: on 401, callers invoke `attemptRefresh()`, which
 * POSTs the in-memory refresh token to `${API_BASE_URL}/v1/auth/refresh`.
 * The shim captures the rotated tokens; we observe the store delta to
 * decide success/failure. A terminal failure (invalid_grant / 401)
 * restarts sign-in in the current tab. In-flight dedup and a 30s window pace
 * the rotation chain so a transient 401 storm doesn't burn the refresh
 * token on every concurrent caller.
 *
 * Multi-tab caveat: tokens live in this module's closure, not
 * `localStorage`, so a new tab does not inherit a sibling tab's
 * session — it runs its own WorkOS exchange on load. The
 * cross-tab `storage`-event coordination that used to live here was
 * removed with the localStorage tokens. A future BroadcastChannel
 * bridge is tracked under the "Bulletproof XSS auth handling" issue.
 */
import { API_BASE_URL, AUTH_REFRESH_URL, AUTH_REQUIRED } from "./featureFlags.js";
import {
    clearTokens,
    getAuthIdentityEpoch,
    onAuthIdentityChange,
    getAccessToken as storeGetAccessToken,
    getRefreshToken as storeGetRefreshToken,
    setTokens,
} from "./authTokenStore.js";
import { broadcastClear, broadcastTokens } from "./authBroadcast.js";
import { clearKernelAuthorHistory } from "./kernelAuthorship.js";
import { clearRememberedRenderHosts } from "./renderSafety.js";

export function getAccessToken() {
    return storeGetAccessToken();
}

export function getRefreshToken() {
    return storeGetRefreshToken();
}

// Legacy reader. The dashboard never wrote `tenant_id` to localStorage
// (see clearUserData.js:28-40); tenant resolution goes through
// `getCurrentTenantIdSync()` against the cached `/v1/me` response.
// Retained as a null-returning stub so existing call sites don't break
// during the migration; remove with the bulletproof-XSS follow-up.
export function getTenantId() {
    return null;
}

export function clearAuthState() {
    clearTokens();
    // Auth state is tab-local. The compatibility seam deliberately emits no
    // profile-wide payload because even a clear bearer leaks credentials.
    broadcastClear();
}

// Decode the payload section of a JWT without verifying the
// signature. Used to extract the `sid` (session id) claim for the
// WorkOS logout URL — purely informational, not a security
// boundary.
function decodeJwtPayload(jwt) {
    if (!jwt || typeof jwt !== "string") return null;
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    try {
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
        const json = atob(padded);
        // JWT payloads can contain UTF-8; atob returns latin1 bytes.
        // Round-trip via TextDecoder to recover unicode safely.
        const bytes = Uint8Array.from(json, (c) => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return null;
    }
}

// Sign the user out:
//   1. Read the current access_token's `sid` claim (if any).
//   2. Clear the in-process token store.
//   3. End the IdP session and bounce back to the dashboard:
//        - docker / local-auth dev builds → the Keycloak `dev` realm
//          end-session endpoint (mirrors the login-side USE_LOCAL_AUTH
//          branch in authBootstrap.js). Without this branch a Keycloak
//          `sid` plus a set VITE_WORKOS_AUTH_URL sent sign-out to WorkOS
//          with a session it doesn't recognize, so the flow never
//          completed and the Keycloak SSO session stayed alive (O-2472).
//        - otherwise → WorkOS logout (kills the IDP session, bounces
//          back to `return_to`).
//      Without a sessionId (and no local-auth) we just reload —
//      `authBootstrap` re-runs and either redirects to authorize or
//      surfaces the missing-token error, depending on env.
export function logout() {
    _signingOut = true;
    clearRefreshRetry();
    if (_signInTimer !== null) clearTimeout(_signInTimer);
    _signInTimer = null;

    const token = getAccessToken();
    const payload = decodeJwtPayload(token);
    const sessionId = payload?.sid ?? null;

    clearKernelAuthorHistory();
    clearRememberedRenderHosts();
    clearAuthState();

    const env =
        typeof import.meta !== "undefined" && import.meta.env
            ? import.meta.env
            : {};

    // Docker / local-auth dev builds authenticate against the Keycloak `dev`
    // realm, not WorkOS — so they must log out against Keycloak too. Gated
    // the same way as authBootstrap's USE_LOCAL_AUTH: VITE_LOCAL_AUTH_URL is
    // only ever set in envs/.env.docker (so cloud builds skip this branch),
    // and env.DEV is defense-in-depth. VITE_LOCAL_AUTH_URL is the realm OIDC
    // base (…/protocol/openid-connect); `/logout` is the RP-initiated
    // end-session endpoint. We pass client_id + post_logout_redirect_uri (no
    // id_token_hint — the store keeps only access/refresh, so Keycloak shows
    // a one-click logout confirmation; see the O-2472 id_token_hint
    // follow-up).
    const localAuthUrl = env.VITE_LOCAL_AUTH_URL || "";
    if (localAuthUrl && env.DEV) {
        const url = new URL(`${localAuthUrl.replace(/\/$/, "")}/logout`);
        url.searchParams.set(
            "client_id",
            env.VITE_LOCAL_AUTH_CLIENT_ID || "api-client",
        );
        url.searchParams.set("post_logout_redirect_uri", window.location.origin);
        window.location.href = url.toString();
        return;
    }

    const workosAuthUrl = env.VITE_WORKOS_AUTH_URL || "";

    if (sessionId && workosAuthUrl) {
        const url = new URL(`${workosAuthUrl}/user_management/sessions/logout`);
        url.searchParams.set("session_id", sessionId);
        url.searchParams.set("return_to", window.location.origin);
        window.location.href = url.toString();
        return;
    }

    // Fall back to a hard reload so authBootstrap re-runs from a
    // clean state (no tokens → redirect to authorize, or in dev
    // mode just refreshes the page).
    window.location.assign(window.location.origin);
}

// Refresh deduplication. Two layers:
//
//   1. `_refreshInFlight` — concurrent callers awaiting a single
//      in-progress refresh share the same promise.
//   2. `_lastRefreshAt` — for ~30s after a refresh attempt (success
//      or failure), reuse only a successful result instead of
//      triggering a new refresh. WorkOS rotates refresh_tokens on
//      each use, so the second refresh-with-the-now-invalidated
//      -refresh-token would 401, cascading back to the caller. 30s
//      covers slow in-flight 401s from before the refresh lands and
//      paces failed-refresh retries so we don't burn the
//      refresh_token in a tight loop.
//
// The cross-tab `storage`-event coordination that used to live here
// is gone with the localStorage tokens. Each tab now refreshes on its
// own; with WorkOS's strict rotation that means a burst of tab opens
// can invalidate older sessions. Tracked as a follow-up.
let _refreshInFlight = null;
let _lastRefreshAt = -Infinity;
let _lastRefreshedAccess = null;
let _lastRefreshIdentity = null;
let _refreshRetryTimer = null;
let _terminalRefreshHandled = false;
let _signInTimer = null;
// Set once the user signs out. Clearing the token store makes every mounted
// caller refetch, and those refetches reach attemptRefresh with no credentials
// left. The recovery redirect that follows would cancel the still-in-flight
// sign-out navigation and leave the IdP session alive.
let _signingOut = false;

// Sign-out clears the token store, which remounts every tenant-derived
// surface while the IdP navigation is still in flight. The root reads this
// to render nothing instead of surfaces that fail on the missing identity.
export function isSigningOut() {
    return _signingOut;
}
const RECENT_REFRESH_MS = 30_000;
// A stalled refresh is the worst hang: every 401'd request fans into this one
// shared promise, so if the refresh never responds, ALL callers wait forever.
// Bound it — on timeout the refresh rejects, settles `_refreshInFlight`, and
// callers fall through to the (still-401) original response instead of spinning.
const REFRESH_TIMEOUT_MS = 15_000;

// Distinguishes "refresh failed transiently" (try again later) from
// "session is permanently dead" (user must re-login). Signals:
//
//   - HTTP 401 on the refresh call itself → refresh_token rejected.
//   - WorkOS `invalid_grant` or "Session has already ended" anywhere
//     in the response body. The gateway currently wraps these in 500
//     responses, so we match on the body substring rather than trust
//     the status — once the gateway maps invalid_grant to 401 the 401
//     branch above will catch the same thing.
function isTerminalRefreshError(status, body) {
    if (status === 401) return true;
    if (typeof body !== "string") return false;
    return body.includes("invalid_grant")
        || body.includes("Session has already ended");
}

function clearRefreshRetry() {
    if (_refreshRetryTimer !== null) clearTimeout(_refreshRetryTimer);
    _refreshRetryTimer = null;
}

onAuthIdentityChange(clearRefreshRetry);

function handleTerminalRefresh() {
    if (_signingOut) return;
    if (!AUTH_REQUIRED || _terminalRefreshHandled || typeof window === "undefined") return;
    _terminalRefreshHandled = true;
    const identity = getAuthIdentityEpoch();
    clearRefreshRetry();
    // Re-run bootstrap in this tab, retaining the view and bypassing an old
    // exchange failure marker. An expired app session need not end IdP SSO.
    _signInTimer = setTimeout(() => {
        _signInTimer = null;
        if (getAuthIdentityEpoch() !== identity) {
            _terminalRefreshHandled = false;
            return;
        }
        const url = new URL(window.location.pathname + window.location.search + window.location.hash, window.location.origin);
        url.searchParams.set("reset_auth", "1");
        clearAuthState();
        window.location.assign(url.toString());
    }, 0);
}

export async function attemptRefresh() {
    if (_refreshInFlight) return _refreshInFlight;
    const refresh = getRefreshToken();
    if (!refresh) {
        handleTerminalRefresh();
        return null;
    }
    if (_lastRefreshIdentity === getAuthIdentityEpoch() && Date.now() - _lastRefreshAt < RECENT_REFRESH_MS) {
        // A failed refresh must not advertise the rejected token as a success.
        const exp = decodeJwtPayload(_lastRefreshedAccess)?.exp;
        const usable = typeof exp !== "number" || exp * 1000 > Date.now();
        return usable && _lastRefreshedAccess === getAccessToken() ? _lastRefreshedAccess : null;
    }
    clearRefreshRetry();
    _lastRefreshedAccess = null;
    const identity = getAuthIdentityEpoch();

    const refreshUrl = AUTH_REFRESH_URL || `${API_BASE_URL.replace(/\/$/, "")}/v1/auth/refresh`;

    _refreshInFlight = (async () => {
        const ctrl = new AbortController();
        const timer = setTimeout(
            () => ctrl.abort(new DOMException("Refresh timed out", "TimeoutError")),
            REFRESH_TIMEOUT_MS,
        );
        try {
            const accessBefore = getAccessToken();
            const res = await fetch(refreshUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: refresh }),
                signal: ctrl.signal,
            });
            if (!res.ok) {
                const text = await res.text().catch(() => "");
                console.warn(`[authBridge] refresh ${res.status}: ${text.slice(0, 200)}`);
                if (getAuthIdentityEpoch() === identity && isTerminalRefreshError(res.status, text)) {
                    handleTerminalRefresh();
                }
                return null;
            }
            // Normal path: the shim peeled tokens out of the body
            // into the store before we observed the response. Read
            // the body to release the connection and to have a
            // payload available for the defensive fallback below.
            const bodyText = await res.text().catch(() => "");
            let accessAfter = getAccessToken();
            if (!accessAfter || accessAfter === accessBefore) {
                if (getAuthIdentityEpoch() !== identity) return null;
                // Defensive fallback: the shim isn't in the call
                // chain (a future main.jsx boot-order regression, or
                // a test path that didn't install it). Parse the body
                // ourselves and prime the store + broadcast to peers
                // so a successful 2xx refresh isn't silently
                // classified as a no-op.
                try {
                    const parsed = JSON.parse(bodyText);
                    const access = parsed?.access_token ?? parsed?.accessToken;
                    const refresh =
                        parsed?.refresh_token ?? parsed?.refreshToken ?? parsed?.refresh;
                    if (typeof access === "string" && access.length > 0) {
                        setTokens({ access, refresh: refresh ?? undefined });
                        broadcastTokens({ access, refresh, user: null });
                        accessAfter = access;
                    }
                } catch {}
            }
            if (!accessAfter || accessAfter === accessBefore) {
                console.warn("[authBridge] refresh response did not advance the store");
                return null;
            }
            _lastRefreshedAccess = accessAfter;
            return accessAfter;
        } catch (err) {
            console.warn("[authBridge] refresh threw", err);
            return null;
        } finally {
            clearTimeout(timer);
            // Set in finally so even a failed refresh paces the next
            // attempt — otherwise we'd hammer WorkOS each 401 and
            // burn through the rotation chain.
            _lastRefreshAt = Date.now();
            _lastRefreshIdentity = _lastRefreshedAccess ? getAuthIdentityEpoch() : identity;
            _refreshInFlight = null;
            if (!_lastRefreshedAccess && !_terminalRefreshHandled
                && getAuthIdentityEpoch() === identity && getRefreshToken() === refresh) {
                _refreshRetryTimer = setTimeout(() => {
                    _refreshRetryTimer = null;
                    if (getAuthIdentityEpoch() === identity && getRefreshToken() === refresh) {
                        void attemptRefresh();
                    }
                }, RECENT_REFRESH_MS);
            }
        }
    })();
    return _refreshInFlight;
}

// How close to `exp` a token counts as stale. Generous relative to the
// 5-minute access-token lifetime: covers clock skew plus the request's
// own flight time, and a refresh 30s early costs nothing thanks to the
// dedup above.
const TOKEN_EXPIRY_SKEW_MS = 30_000;

/**
 * Return an access token that is not expired (or about to expire),
 * refreshing first when needed.
 *
 * Exists because not every backend route answers a stale token with a
 * 401 the response-side handlers can react to — the LLM proxy rejection
 * surfaced as HTTP 200 + empty body, so chat turns sent >5 min into a
 * session silently died with the store token expired (O-2046). Callers
 * on such routes invoke this *before* sending instead.
 *
 * Tokens without a decodable `exp` claim are passed through untouched —
 * refreshing on undecodable tokens would fire on every call for any
 * future non-JWT credential. On refresh failure, an unexpired token can
 * still be used; an expired token returns null while recovery is retried.
 */
export async function ensureFreshAccessToken() {
    const token = getAccessToken();
    const exp = decodeJwtPayload(token)?.exp;
    if (!token) {
        if (getRefreshToken()) return attemptRefresh();
        handleTerminalRefresh();
        return null;
    }
    if (typeof exp !== "number") return token;
    if (exp * 1000 - TOKEN_EXPIRY_SKEW_MS > Date.now()) return token;
    const refreshed = await attemptRefresh();
    return refreshed ?? (exp * 1000 > Date.now() && getAccessToken() === token ? token : null);
}

export class AuthMissingError extends Error {
    constructor() {
        super(
            "Your session is reconnecting. Please wait for sign-in to complete, then try again.",
        );
        this.name = "AuthMissingError";
    }
}

// Test teardown must not leave a background refresh in the next test's session.
export function _resetForTests() {
    clearRefreshRetry();
    if (_signInTimer !== null) clearTimeout(_signInTimer);
    _signInTimer = null;
    _refreshInFlight = null;
    _lastRefreshAt = -Infinity;
    _lastRefreshIdentity = null;
    _lastRefreshedAccess = null;
    _terminalRefreshHandled = false;
    _signingOut = false;
}
